import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { theme } from "./theme";
import { useThemeMode } from "./context/ThemeContext";
import i18n, { type SupportedLanguage } from "./i18n";
import { TRANSLATED_LANGUAGES, isFullyTranslated, languagePickerAvailable } from "./lib/languages";
import { useCurrentUser } from "./auth/useCurrentUser";
import { ClientLayout } from "./components/ClientLayout";
import { InactivityWatcher } from "./components/InactivityWatcher";
import { LoginPage } from "./pages/Login";
import { InviteRedeemPage } from "./pages/InviteRedeem";
import { SupplierQuestionnairePage } from "./pages/SupplierQuestionnaire";
import { SubmissionSharePage } from "./pages/SubmissionShare";
import { AssuranceSharePage } from "./pages/AssuranceShare";
import { ConsultantPortfolioPage } from "./pages/ConsultantPortfolio";
import { AuditorPortal } from "./pages/AuditorPortal";
import { SignedOutPage } from "./pages/SignedOut";
import { ClientReleasePage } from "./pages/ClientRelease";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicy";
import { PartnerApplyPage } from "./pages/PartnerApplyPage";
import { OnboardingPage } from "./pages/Onboarding";
import { MfaEnrolPage } from "./pages/MfaEnrol";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/ResetPassword";
import { api, ApiError, clearLocalSession, hasLocalSession as hasLocalSessionNow, onLocalSessionEnded, popLocalSession, startRoleSwitch } from "./api/client";
import { Spinner } from "./components/Spinner";
import type { CurrentUser } from "./auth/useCurrentUser";

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: theme.color.bg, color: theme.color.muted, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: theme.font, fontSize: 13 }}>
      {children}
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────

const CONSULTANT_DOMAIN = import.meta.env.VITE_CONSULTANT_DOMAIN?.toLowerCase();

export function App() {
  // Read so that switching to or from the colour-blind theme re-renders every page with its
  // colours (lib/colourBlind.ts); theme tokens alone would not need it.
  useThemeMode();
  const location             = useLocation();
  const navigate             = useNavigate();
  const isEntraAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const hasLocalSession      = hasLocalSessionNow();
  const { user, loading, organisationNotRegistered, signInRefused, refetch } = useCurrentUser();

  // The role picked at sign-in is read once into state and then forgotten.
  // "Client" on a consultant account means "open my own organisation's portal"
  // (see OwnOrgSwitch below). Read in the initialiser, cleared in an effect, so
  // StrictMode's double render cannot consume it before it is kept.
  const [pendingClientSwitch, setPendingClientSwitch] = useState(
    () => sessionStorage.getItem("abacas.intendedRole") === "client",
  );
  useEffect(() => { sessionStorage.removeItem("abacas.intendedRole"); }, []);
  const [openSelfOrgSetup, setOpenSelfOrgSetup] = useState(false);
  const [switchNotice, setSwitchNotice] = useState<string | null>(null);

  const handleAutoSignOut = useCallback(() => {
    clearLocalSession();
    navigate("/signedout?reason=inactivity");
    instance.logoutRedirect({ onRedirectNavigate: () => false });
  }, [navigate, instance]);

  // The session is shared between tabs, so a sign-out in one — by hand or after
  // ten idle minutes — closes this one too rather than leaving it open on a
  // machine whose user believes they have signed out.
  useEffect(() => onLocalSessionEnded(() => {
    instance.logoutRedirect({ onRedirectNavigate: () => false });
    navigate("/signedout");
  }), [navigate, instance]);

  // Allow /signedout and /invite to render regardless of auth state
  if (location.pathname === "/signedout") {
    return <SignedOutPage />;
  }

  if (location.pathname === "/invite") {
    return <InviteRedeemPage />;
  }

  if (location.pathname === "/supplier-questionnaire") {
    return <SupplierQuestionnairePage />;
  }

  // A competent authority reading one shared submission. No session, ever.
  if (location.pathname === "/submission-share") {
    return <SubmissionSharePage />;
  }

  // An insurer, broker or customer reading a published assurance pack. No session, ever.
  if (location.pathname === "/assurance-share") {
    return <AssuranceSharePage />;
  }

  if (location.pathname === "/partner/apply") {
    return <PartnerApplyPage />;
  }

  if (location.pathname.startsWith("/release/")) {
    return <ClientReleasePage />;
  }

  if (location.pathname === "/privacy-policy") {
    return <PrivacyPolicyPage />;
  }

  if (location.pathname === "/forgot-password") {
    return <ForgotPasswordPage />;
  }

  if (location.pathname === "/reset-password") {
    return <ResetPasswordPage />;
  }

  if (!isEntraAuthenticated && !hasLocalSession && inProgress !== InteractionStatus.HandleRedirect) {
    return <LoginPage onLocalLogin={refetch} />;
  }

  if (loading) {
    return <FullScreenMessage>Loading your account…</FullScreenMessage>;
  }

  if (signInRefused) {
    return (
      <FullScreenMessage>
        <div style={{ textAlign: "center", maxWidth: 440, padding: "0 16px" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            Sign in with your work account
          </div>
          <div style={{ lineHeight: 1.6, marginBottom: 16 }}>{signInRefused}</div>
          <button
            onClick={() => instance.logoutRedirect({ onRedirectNavigate: () => false })}
            style={{ padding: "8px 16px", borderRadius: 3, background: "transparent", color: theme.color.muted, border: `1px solid ${theme.color.border}`, cursor: "pointer", fontSize: 13 }}
          >
            Sign out and use another account
          </button>
        </div>
      </FullScreenMessage>
    );
  }

  if (organisationNotRegistered) {
    // Client whose tenant isn't registered yet — existing registration flow.
    const signedInEmail = accounts[0]?.username?.toLowerCase() ?? "";
    const isConsultantAccount = CONSULTANT_DOMAIN
      ? signedInEmail.endsWith(`@${CONSULTANT_DOMAIN}`)
      : false;

    if (isConsultantAccount) {
      return (
        <FullScreenMessage>
          <div style={{ textAlign: "center", maxWidth: 440, padding: "0 16px" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
              Consultant account not configured
            </div>
            <div style={{ lineHeight: 1.6 }}>
              Your account (<strong>{accounts[0]?.username}</strong>) was not recognised
              as a consultant. Please contact your Abacas administrator to verify
              your account has been provisioned with the correct role.
            </div>
          </div>
        </FullScreenMessage>
      );
    }

    return (
      <FullScreenMessage>
        <div style={{ textAlign: "center", maxWidth: 440, padding: "0 16px" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            Access by invitation only
          </div>
          <div style={{ lineHeight: 1.6 }}>
            Your organisation (<strong>{accounts[0]?.username}</strong>) isn't registered on this platform.
            Please contact your consultant to receive an invitation.
          </div>
        </div>
      </FullScreenMessage>
    );
  }

  if (!user) {
    // If still Entra-authenticated but user failed to load, don't route back to
    // LoginPage — that triggers loginRedirect → redirect loop. Show an error so
    // the user can retry or sign out instead.
    if (isEntraAuthenticated) {
      return (
        <FullScreenMessage>
          <div style={{ textAlign: "center", maxWidth: 420, padding: "0 16px" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
              Could not load your account
            </div>
            <div style={{ lineHeight: 1.6, marginBottom: 16 }}>
              Your Microsoft sign-in was recognised but your user profile could not be loaded. This is usually a temporary issue.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button
                onClick={refetch}
                style={{ padding: "8px 16px", borderRadius: 3, background: theme.color.accent, color: "#fff", border: "none", cursor: "pointer", fontSize: 13 }}
              >
                Retry
              </button>
              <button
                onClick={() => instance.logoutRedirect({ onRedirectNavigate: () => false })}
                style={{ padding: "8px 16px", borderRadius: 3, background: "transparent", color: theme.color.muted, border: `1px solid ${theme.color.border}`, cursor: "pointer", fontSize: 13 }}
              >
                Sign out
              </button>
            </div>
          </div>
        </FullScreenMessage>
      );
    }
    return <LoginPage onLocalLogin={refetch} />;
  }

  // A password account whose authenticator app is not enrolled yet holds a
  // session the API restricts to enrolment — so that is all we show.
  if (user.mfaEnrolmentRequired) {
    return <MfaEnrolPage user={user} onDone={refetch} />;
  }

  if (user.role === "consultant") {
    // A consultant who chose "Client" at sign-in is switched straight into their
    // own organisation's client session; the client session then persists in the
    // tab on its own, so nothing else needs remembering.
    if (pendingClientSwitch) {
      return (
        <OwnOrgSwitch
          onDone={(outcome) => {
            setPendingClientSwitch(false);
            if (outcome === "no_self_org") setOpenSelfOrgSetup(true);
            if (outcome === "failed") setSwitchNotice("Could not open your company's portal, so you are in the consultant workspace instead.");
            if (outcome === "switched") refetch();
          }}
        />
      );
    }

    return (
      <>
        <LanguageSyncEffect language={user.language} />
        <InactivityWatcher onExpire={handleAutoSignOut} />
        <ConsultantPortfolioPage
          user={user}
          onSwitchRole={refetch}
          openSelfOrgSetup={openSelfOrgSetup}
          initialNotice={switchNotice}
        />
      </>
    );
  }

  if (user.role === "auditor") {
    const handleBackToConsultant = () => {
      popLocalSession();
      refetch();
    };

    return (
      <>
        <LanguageSyncEffect language={user.language} />
        <InactivityWatcher onExpire={handleAutoSignOut} />
        <AuditorPortal user={user} onBackToConsultant={user.canActAsAuditor ? handleBackToConsultant : undefined} />
      </>
    );
  }

  // A member of a Jersey edition organisation works on jsy.abacassecurity.com (backend lib/editions.ts).
  // Consultancy staff in their own organisation stay here: that organisation is never a Jersey one.
  if (user.edition === "jersey" && user.editionUrl && !user.actingAsOwnOrg && !user.viewedBy) {
    return <SendToEdition url={user.editionUrl} />;
  }

  if (!user.onboardingComplete && !user.viewedBy) {
    return <OnboardingPage onComplete={refetch} />;
  }

  // Consultancy staff inside their own organisation's portal: the same client
  // shell, with a way back to the consultant workspace (ends the client session
  // and falls back to the Entra consultant identity — as the auditor switch does).
  // A platform administrator viewing a demo organisation as one of its people returns the same way.
  const handleBackToConsultant = user.actingAsOwnOrg || user.viewedBy
    ? () => { popLocalSession(); refetch(); }
    : undefined;

  return (
    <>
      <LanguageSyncEffect language={user.language} />
      {/* Viewing as a demo member is not that member's first sign-in: never ask, never save their language. */}
      {!user.viewedBy && <FirstLoginLanguagePicker language={user.language} />}
      <ClientLayout user={user} onAutoSignOut={handleAutoSignOut} onBackToConsultant={handleBackToConsultant} />
    </>
  );
}

// ── Jersey edition members ───────────────────────────────────────────────
// The session is per host, so the member signs in again on the Jersey site; the
// path they asked for (a deep link from an email, an OAuth return) is kept.

function SendToEdition({ url }: { url: string }) {
  const location = useLocation();
  const target = `${url.replace(/\/+$/, "")}${location.pathname}${location.search}`;
  useEffect(() => {
    const t = setTimeout(() => window.location.replace(target), 1200);
    return () => clearTimeout(t);
  }, [target]);
  return (
    <FullScreenMessage>
      <div style={{ textAlign: "center", maxWidth: 440, padding: "0 16px", lineHeight: 1.6 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Your organisation uses Abacas Jersey</div>
        <div style={{ marginBottom: 14 }}>Taking you to your compliance portal.</div>
        <a href={target} style={{ color: theme.color.accent, fontWeight: 600 }}>Continue</a>
      </div>
    </FullScreenMessage>
  );
}

// ── Consultant → own-organisation client session ──────────────────────────
// Runs once after a consultant signed in as "Client": asks the API for a client
// session on the consultancy's own organisation and installs it. Failing that,
// the consultant lands in their workspace — with the setup dialog open when the
// consultancy has no organisation of its own yet.

function OwnOrgSwitch({ onDone }: { onDone: (outcome: "switched" | "no_self_org" | "failed") => void }) {
  const navigate = useNavigate();
  const started = useRef(false); // one session per switch, even under StrictMode's double effect
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    startRoleSwitch<{ accessToken: string }>("/auth/select-role", { role: "client" })
      .then(() => {
        navigate("/actions", { replace: true });
        onDone("switched");
      })
      .catch((e) => {
        onDone(e instanceof ApiError && e.code === "no_self_org" ? "no_self_org" : "failed");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FullScreenMessage>
      <div style={{ textAlign: "center" }}>
        <Spinner size={28} />
        <div style={{ marginTop: 14 }}>Opening your compliance portal…</div>
      </div>
    </FullScreenMessage>
  );
}

// ── Sync i18n with the user's saved language on every login ──────────────

function LanguageSyncEffect({ language }: { language: string | null }) {
  useEffect(() => {
    // Only a fully translated language is applied; a stored preference for another stays on file.
    if (isFullyTranslated(language) && language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, [language]);
  return null;
}

// ── First-login language picker modal ─────────────────────────────────────
// Only shown when language is null (never explicitly chosen by this user).
// Once saved server-side, it won't reappear on any browser or device.

function FirstLoginLanguagePicker({ language }: { language: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(language === null && languagePickerAvailable());
  const [selected, setSelected] = useState<SupportedLanguage>("en");
  const [saving, setSaving] = useState(false);

  const saveAndClose = async (lang: SupportedLanguage) => {
    setSaving(true);
    try {
      await api.patch("/users/me/language", { language: lang });
      await i18n.changeLanguage(lang);
    } catch { /* non-fatal */ }
    setOpen(false);
  };

  // Cancel saves nothing: the platform stays in English for now and the question is asked again
  // next time. Only Continue records a choice.
  const dismiss = () => setOpen(false);

  const confirm = () => saveAndClose(selected);

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--c-panel)", borderRadius: 3, padding: 32, maxWidth: 480, width: "90%", boxShadow: "var(--c-sheetShadow)" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--c-heading)", marginBottom: 6 }}>{t("firstLogin.welcome")}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--c-text)", marginBottom: 4 }}>{t("firstLogin.chooseLanguage")}</div>
        <div style={{ fontSize: 13, color: "var(--c-subtle)", marginBottom: 20 }}>{t("firstLogin.languageHint")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 }}>
          {TRANSLATED_LANGUAGES.map((lang) => {
            const active = selected === lang;
            return (
              <button
                key={lang}
                onClick={() => setSelected(lang)}
                style={{
                  padding: "10px 14px",
                  borderRadius: 3,
                  border: `1.5px solid ${active ? "var(--c-accent)" : "var(--c-border)"}`,
                  background: active ? "var(--c-accentBg)" : "transparent",
                  color: active ? "var(--c-accentText)" : "var(--c-text)",
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {t(`language.${lang}`)}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={dismiss}
            disabled={saving}
            style={{ padding: "9px 18px", borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-muted)", fontSize: 13, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={confirm}
            disabled={saving}
            style={{ padding: "9px 18px", borderRadius: 3, border: "none", background: "var(--c-accent)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}
          >
            {saving ? t("common.saving") : t("firstLogin.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
